/**
 * Escuro, claro ou igual ao sistema (`C-19`, ADR-055) — no pé da lateral, ao lado do nome de
 * quem está logado, e no menu do celular.
 *
 * Mora com a pessoa, e não no topo da tela, porque é uma escolha dela: muda uma vez e fica. A
 * troca vale na hora, e a escolha vai para a conta — o próximo login, no computador ou no
 * celular, já abre no tema certo. Três rádios de verdade, só com outra roupa: as setas do
 * teclado trocam de opção e o leitor de tela diz "Tema da tela, Claro, 2 de 3".
 */
import { useEffect, useId, useState } from "react";

import { useSessao } from "../api/contexto-sessao";
import type { Tema } from "../tema";
import { Icone } from "./icones";

const OPCOES: {
  tema: Tema;
  rotulo: string;
  dica: string;
  Desenho: () => React.JSX.Element;
}[] = [
  {
    tema: "escuro",
    rotulo: "Escuro",
    dica: "O cockpit de sempre — o da sala de monitoramento",
    Desenho: Icone.Lua,
  },
  {
    tema: "claro",
    rotulo: "Claro",
    dica: "Para ambiente iluminado. As imagens de pórtico continuam sobre fundo escuro",
    Desenho: Icone.Sol,
  },
  {
    tema: "sistema",
    rotulo: "Sistema",
    dica: "Igual ao computador ou ao celular, e muda junto com ele",
    Desenho: Icone.Monitor,
  },
];

export function SeletorDeTema({ grande = false }: { grande?: boolean }) {
  const { quem, trocarTema } = useSessao();
  const nome = useId();
  const [falhou, definirFalhou] = useState(false);

  useEffect(() => {
    if (!falhou) return;
    const id = window.setTimeout(() => definirFalhou(false), 6000);
    return () => window.clearTimeout(id);
  }, [falhou]);

  if (!quem) return null;

  const escolher = (tema: Tema) => {
    definirFalhou(false);
    trocarTema(tema).catch(() => definirFalhou(true));
  };

  return (
    <fieldset className="grid min-w-0 gap-1">
      <legend className="mb-1 text-rotulo tracking-[0.06em] text-tinta-3 uppercase">
        Tema da tela
      </legend>
      <div className="grid grid-cols-3 gap-0.5 rounded-botao border border-linha bg-painel-2 p-0.5">
        {OPCOES.map(({ tema, rotulo, dica, Desenho }) => (
          <label
            key={tema}
            title={dica}
            className={`flex cursor-pointer items-center justify-center gap-1 rounded-[4px] border border-transparent px-0.5 text-[11px] [&_svg]:size-3.5 [&_svg]:shrink-0 text-tinta-3 transition-colors hover:text-tinta-2 has-checked:border-linha has-checked:bg-painel has-checked:font-semibold has-checked:text-tinta has-focus-visible:outline-2 has-focus-visible:outline-azul ${
              grande ? "min-h-10" : "min-h-7"
            }`}
          >
            <input
              type="radio"
              name={nome}
              value={tema}
              checked={quem.tema === tema}
              onChange={() => escolher(tema)}
              className="sr-only"
            />
            <Desenho />
            {rotulo}
          </label>
        ))}
      </div>
      {falhou && (
        <span role="status" className="text-[10.5px] text-vermelho">
          Não deu para guardar o tema. Tente de novo.
        </span>
      )}
    </fieldset>
  );
}
